/**
 * User Festival Status Types and Utilities
 * Web version - delegates to shared utilities with platform-specific extensions
 */

import {
	getStatusLabel as getSharedStatusLabel,
	getStatusIconConfig,
	getValidTransitions,
	getNextStatus,
	shouldAutoTransition,
	mapLegacyStatus,
	USER_FESTIVAL_STATUS,
	LEGACY_STATUS,
	type UserFestivalStatus,
} from "@workspace/shared-utils";

// Re-export shared types and constants
export { 
	USER_FESTIVAL_STATUS, 
	LEGACY_STATUS,
	type UserFestivalStatus,
	getValidTransitions,
	getNextStatus,
	shouldAutoTransition,
	mapLegacyStatus,
};

/**
 * Get display label for status - Web specific version
 * Web shows "Want to Go" for null status (call-to-action for split button UI)
 */
export function getStatusLabel(
	status: UserFestivalStatus,
	isPast: boolean = false,
): string {
	// Web-specific override for null status
	if (status === null) {
		return isPast ? "Wanted to Go" : "Want to Go";
	}
	// Delegate to shared utility for all other statuses
	return getSharedStatusLabel(status, isPast);
}

/**
 * Get icon name for status (Remix icons) - Web specific mapping
 */
export function getStatusIconName(status: UserFestivalStatus): {
	icon: "pushpin-2" | "calendar-check" | "live";
	filled: boolean;
} {
	const config = getStatusIconConfig(status);
	
	// Map generic icon names to web-specific Remix icon names
	switch (config.name) {
		case "pin":
			return { icon: "pushpin-2", filled: config.filled };
		case "calendar-check":
			return { icon: "calendar-check", filled: config.filled };
		case "live":
			return { icon: "live", filled: config.filled };
		default:
			return { icon: "pushpin-2", filled: false };
	}
}

/**
 * Get color classes for status - Web specific (Tailwind classes)
 */
export function getStatusColorClass(status: UserFestivalStatus): string {
	switch (status) {
		case USER_FESTIVAL_STATUS.WANT_TO_GO:
			return "text-blue-500";
		case USER_FESTIVAL_STATUS.GOING:
			return "text-green-500";
		case USER_FESTIVAL_STATUS.WENT:
			return "text-gray-500";
		case USER_FESTIVAL_STATUS.WATCHED_ONLINE:
			return "text-ruby-500";
		default:
			return "text-muted-foreground";
	}
}
